import React, { useRef, useState, useEffect } from "react"
import { Button } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import useDebounce from "../../../app/hooks/useDebounce.jsx"
import { BaseMapSelector } from "../../atoms/MapSelector/BaseMapSelector.jsx"
import { Search, EmptyCard } from "../../atoms/index.jsx"
import { ILLUSTRATION_CONSTANTS } from "../../../constants/ImageConstants.js"
import { Popup } from "../../atoms/Popup/index.jsx"

/**
 * PopUpMapSelector – reusable map-picker popup.
 *
 * Props:
 *  show             {boolean}                    – whether popup is visible
 *  onCancel         {() => void}                 – called when popup should close
 *  onSave           {({ lat, lng }) => void}     – called with selected coords on Save
 *  initialLat       {number|null}                – pre-selected latitude
 *  initialLng       {number|null}                – pre-selected longitude
 *  hasLocationAlreadySaved {boolean}             – if true, save is only enabled when location changed
 */
export const PopUpMapSelector = ({
  show = false,
  onCancel = () => {},
  onSave = () => {},
  initialLat = null,
  initialLng = null,
  icon
}) => {
  const { t } = useTranslation()

  const DEFAULT_POSITION = { lat: -6.2088, lng: 106.8456 }

  const [locTemp, setLocTemp] = useState(DEFAULT_POSITION)
  const [searchKeyword, setSearchKeyword] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [isSearchEmpty, setIsSearchEmpty] = useState(false)
  const [isActiveSearch, setIsActiveSearch] = useState(false)
  const debouncedSearchKeyword = useDebounce(searchKeyword, 800)
  const searchFormRef = useRef(null)
  const [recenterPos, setRecenterPos] = useState(null)
  const initialSearchValues = { keyword: "" }

  // Initialise map position whenever the popup opens
  useEffect(() => {
    if (!show) return
    if (initialLat && initialLng) {
      setLocTemp({ lat: parseFloat(initialLat), lng: parseFloat(initialLng) })
    } else {
      setLocTemp(DEFAULT_POSITION)
    }
    // reset search state each time popup opens
    setSearchResults([])
    setIsSearchEmpty(false)
    setSearchKeyword("")
  }, [show])

  // Trigger geocode when debounced keyword changes
  useEffect(() => {
    if (debouncedSearchKeyword && debouncedSearchKeyword.trim() !== "") {
      performSearch(debouncedSearchKeyword).then()
    }
  }, [debouncedSearchKeyword])

  const performSearch = async (keyword) => {
    setIsActiveSearch(true)
    setSearchResults([])
    setIsSearchEmpty(false)
    try {
      const geocoder = new window.google.maps.Geocoder()
      await geocoder.geocode({ address: keyword, language: "id" }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          const formatted = results.map((r) => ({
            address: r.formatted_address,
            lat: r.geometry.location.lat(),
            lng: r.geometry.location.lng(),
          }))
          setSearchResults(formatted)
          handleLocationSelect(formatted[0])
        } else {
          setSearchResults([])
          setIsSearchEmpty(true)
        }
      })
    } catch {
      setSearchResults([])
      setIsSearchEmpty(true)
    }
  }

  const handleLocationSelect = (location) => {
    setLocTemp({ lat: location.lat, lng: location.lng })
    setRecenterPos({ lat: location.lat, lng: location.lng })
  }

  const handleSearchLocation = (values) => {
    const { keyword } = values
    setSearchKeyword(keyword)
    if (searchResults.length > 0 && searchFormRef.current) {
      searchFormRef.current.setFieldValue("keyword", searchResults[0].address)
    }
  }

  const handleCurrentLocation = () => {
    navigator?.geolocation.getCurrentPosition(
      ({ coords: { latitude: lat, longitude: lng } }) => {
        setRecenterPos({ lat, lng })
      },
      null
    )
  }

  const hasInitialLocation = initialLat !== null && initialLng !== null

  const hasLocationChanged =
    locTemp.lat !== parseFloat(initialLat) || locTemp.lng !== parseFloat(initialLng)

  const isSaveDisabled = hasInitialLocation && !hasLocationChanged

  const handleSave = () => {
    onSave({ lat: locTemp.lat, lng: locTemp.lng, address: searchFormRef.current?.values?.keyword || undefined })
    setIsActiveSearch(false)
  }

  const handleCancel = () => {
    setSearchKeyword("")
    setSearchResults([])
    setIsSearchEmpty(false)
    setIsActiveSearch(false)
    if (searchFormRef.current) {
      searchFormRef.current.resetForm()
    }
    onCancel()
  }

  return (
    <Popup
      show={show}
      titleCustom={<h5 className="font-weight-bold">{t("label_pin_location")}</h5>}
      size={"lg"}
      bodyCustomClassName={"h-50"}
      contentClassName={"h-50"}
      closeButton={false}
      onCancel={onCancel}
      showButton={false}
      body={undefined}
      bodyCustom={
        <div className={"position-relative"}>
          {/* Search bar */}
          <div
            className={"filter-container position-absolute zindex-3"}
            style={{ top: "0.8rem", left: "15.5rem", width: "calc(100% - 20.2rem)" }}
          >
            <Search
              placeholder={t("placeholder_insert_location")}
              onSubmitForm={handleSearchLocation}
              onChange={(value) => setSearchKeyword(value)}
              initialValues={initialSearchValues}
              isActiveSearch={isActiveSearch}
              name={"keyword"}
              innerRef={searchFormRef}
              trigerReset={() => {
                searchFormRef.current?.resetForm()
                setSearchResults([])
                setIsSearchEmpty(false)
                setSearchKeyword("")
              }}
              customClass={"w-full"}
            />
            {searchResults.length > 0 && (
              <div
                className="bg-white rounded mt-2 shadow-sm"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className={`cursor-pointer p-4 ${
                      index !== searchResults.length - 1 ? "border-bottom" : ""
                    }`}
                    onClick={() => {
                      handleLocationSelect(result)
                      setSearchResults([])
                      setIsSearchEmpty(false)
                      setIsActiveSearch(false)
                      if (searchFormRef.current) {
                        searchFormRef.current.setFieldValue("keyword", result.address)
                      }
                    }}
                  >
                    <span className="text-dark-75 font-weight-bold font-size-lg">
                      {result.address}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div style={{ position: "relative" }}>
            <BaseMapSelector
              id={"popup-map-selector"}
              latitude={locTemp.lat}
              longitude={locTemp.lng}
              isSelector={true}
              editable={true}
              onLocationSelect={handleLocationSelect}
              zoom={15}
              showCurrentLocationButton={true}
              onCurrentLocationClick={handleCurrentLocation}
              recenterPosition={recenterPos}
              customContainerStyle={{ height: "450px" }}
              icon={icon}
            />
            {isSearchEmpty && (
              <div style={{ position: "absolute", inset: 0, zIndex: 5, background: "#fff" }}>
                <EmptyCard
                  body={t("messages_response_searching_not_found")}
                  image={ILLUSTRATION_CONSTANTS.NOT_FOUND_FARMER}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={"d-flex mt-8"} style={{ gap: 16 }}>
            <Button className={"flex-1"} variant={"outline-primary"} onClick={handleCancel}>
              {t("button_batal")}
            </Button>
            <Button
              disabled={isSaveDisabled}
              className={"flex-1"}
              variant={"primary"}
              onClick={handleSave}
            >
              {t("button_save_id")}
            </Button>
          </div>
        </div>
      }
    />
  )
}
